import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';

interface DragAndDropFileInputProps {}

interface DragAndDropFileUploadProps extends DragAndDropFileInputProps {
    text?: ReactNode;
    onChange?: (files: File[]) => void;
    accept?: Accept;
    multiple?: boolean;
    maxFiles?: number;
    maxSize?: number;
    onFileTooLargeError?: (message: string) => void;
    onFileInvalidTypeError?: (message: string) => void;
    onTooManyFilesError?: (message: string) => void;
}

const DragAndDropFileUpload: FC<DragAndDropFileUploadProps> = (props) => {
    const {
        text,
        onChange,
        accept,
        multiple = false,
        maxFiles,
        maxSize,
        onFileTooLargeError,
        onFileInvalidTypeError,
        onTooManyFilesError,
    } = props;

    const fileRef = useRef(null);
    const [value, setValue] = useState(Date.now());

    useEffect(() => {
        if (!fileRef.current) return;

        fileRef.current.value = '';
    }, [value]);

    const handleChange = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        let hasTooLargeError = false;
        let hasInvalidTypeError = false;
        let hasTooManyFilesError = false;

        fileRejections.forEach((file) => {
            file.errors.forEach((err) => {
                if (err.code === 'file-too-large') {
                    hasTooLargeError = true;
                }

                if (err.code === 'file-invalid-type') {
                    hasInvalidTypeError = true;
                }

                if (err.code === 'too-many-files') {
                    hasTooManyFilesError = true;
                }
            });
        });

        if (hasTooLargeError) {
            onFileTooLargeError?.('One or more files exceed the allowed size.');
        }

        if (hasInvalidTypeError) {
            onFileInvalidTypeError?.('One or more files have an invalid file type.');
        }

        if (hasTooManyFilesError) {
            onTooManyFilesError?.(
                maxFiles ? `You can upload a maximum of ${maxFiles} files only.` : 'You have selected too many files.'
            );
        }

        if (acceptedFiles && acceptedFiles.length > 0) {
            onChange?.([...acceptedFiles]);
            setValue(Date.now());
        }
    };

    return (
        <Dropzone
            onDrop={(acceptedFiles, fileRejections) => handleChange(acceptedFiles, fileRejections)}
            accept={accept}
            multiple={multiple}
            maxFiles={maxFiles}
            useFsAccessApi={false}
            maxSize={maxSize}
        >
            {({ getRootProps, getInputProps }) => (
                <div
                    {...getRootProps({
                        className:
                            'relative bg-background-color border border-dashed border-grayscale-300 rounded-[10px] overflow-hidden h-full cursor-pointer',
                    })}
                >
                    <input {...getInputProps()} />

                    <div className="text-center p-6">
                        <div className="text-grayscale-400 font-medium text-sm">
                            <span className="text-primary">Upload a file</span> or drag and drop
                        </div>
                        <div className="text-grayscale-400 text-xs mt-2">{text}</div>
                    </div>
                </div>
            )}
        </Dropzone>
    );
};

export default DragAndDropFileUpload;
